import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { VerifyOtpResponse } from '../models/VerifyOtpResponse';
import { errorModal } from '../models/error-modal';
import { LoginStore } from '../models/login-store';
import { ApplicationFlowProperties } from '../models/navbar-canvasser-detail';
import { DatePipe } from '@angular/common';
import { AuthMethodResponse } from '../models/AuthMethodResponse';
import { VerifyOtpRequest } from '../models/VerifyOtpRequest';
import { RetailerSelectionRequest, RetailerSelectionResponse } from '../models/RetailerSelection';

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {

  private headers = new HttpHeaders({
    'Content-Type': 'application/json',
    'cache-control': 'no-cache',
    'Access-Control-Allow-Origin': '*',
  });

  /**
   * Creates the ApplicationService and injects the HttpClient used for all
   * backend requests. The service is provided in root, so a single instance is
   * shared across the application for authentication and login-store calls.
   * @param http The Angular HttpClient used to issue HTTP requests.
   */
  constructor(    
    private http: HttpClient
  ) { 

  }

  private errorModalLaunch: errorModal = {
    errorLevel: '',
    respCode: '',
    respDesc: '',
  };

  private _errorModalLaunch = new BehaviorSubject<errorModal>(
    this.errorModalLaunch
  );

  readonly errorModalLaunch$ = this._errorModalLaunch.asObservable();

  /**
   * Publishes a new error modal payload to any subscribers of
   * errorModalLaunch$. Typically consumed by a global error modal component to
   * display error details returned from HTTP interceptors or service calls.
   * @param ErrorModal The error modal payload describing level, code and description.
   * @returns void
   */
  broadcastErrorModalLaunch(ErrorModal: errorModal) {
    this._errorModalLaunch.next(ErrorModal);
  }


  private cavanvasserMessage: string = ''

  /**
   * Publishes a canvasser-related message to any subscribers of
   * cavanvasserMessage$. Used by the login flow to broadcast the current
   * canvasser code (or clear it) so other components such as the header can
   * update their displayed information.
   * @param CanvasserMessage The message to broadcast (empty string to clear).
   * @returns void
   */
  broadcastCavanvasserMessage(CanvasserMessage: string) {
    this._cavanvasserMessage.next(CanvasserMessage);
  }

  private _cavanvasserMessage = new BehaviorSubject<string>(
    this.cavanvasserMessage
  );

  readonly cavanvasserMessage$ = this._cavanvasserMessage.asObservable();


  

  /**
   * Submits a canvasser code to the authentication endpoint to determine which
   * authentication method (for example OTP) should be used for the canvasser.
   * Wraps the code in the expected request body and issues a POST against the
   * PplAppsApi Authentication controller.
   * @param canvasserCode The canvasser code entered by the user.
   * @returns An Observable emitting the AuthMethodResponse from the backend.
   */
  submitCanvasserCode(
    canvasserCode: string
  ): Observable<AuthMethodResponse> {

    const body = {
      canvasserCode: canvasserCode
    };

    const url = `${environment.PplAppsApi}/Authentication/authenticate`;
    const httpOptions = { headers: this.headers };
    return this.http.post<AuthMethodResponse>(url, body, httpOptions);
  }

  /**
   * Verifies a one-time password for the supplied canvasser code by posting
   * the request to the Authentication verify-otp endpoint. The response
   * indicates whether the OTP was valid, the remaining attempts, the issued
   * token and any retailer/store information required for the next login stage.
   * @param verifyOtpRequest The canvasser code and OTP to be verified.
   * @returns An Observable emitting the VerifyOtpResponse from the backend.
   */
  verifyOtp(
    verifyOtpRequest: VerifyOtpRequest
  ): Observable<VerifyOtpResponse> {

    const url = `${environment.PplAppsApi}/Authentication/verify-otp`;
    const httpOptions = { headers: this.headers };
    return this.http.post<VerifyOtpResponse>(url, verifyOtpRequest, httpOptions);
  }

  /**
   * Retrieves the list of stores and canvasser capabilities associated with a
   * given retailer for a universal canvasser. Called after the canvasser has
   * selected a retailer in the login flow so the store selection stage can be
   * populated.
   * @param retailerSelectionRequest The canvasser code and selected company code.
   * @returns An Observable emitting the RetailerSelectionResponse with stores and capabilities.
   */
  getStoresByRetailerCode(
    retailerSelectionRequest: RetailerSelectionRequest
  ): Observable<RetailerSelectionResponse> {

    const url = `${environment.PplAppsApi}/Retailer/GetRetailerSelectionsAsync`; 
    const httpOptions = { headers: this.headers };
    return this.http.post<RetailerSelectionResponse>(url, retailerSelectionRequest, httpOptions);
  }

  /**
   * Records a login-store entry for the specified canvasser and store on the
   * backend. Builds a LoginStore payload with the current timestamp formatted
   * in the +02:00 offset expected by the API before issuing the POST request.
   * @param canvasserCode The canvasser code performing the login.
   * @param storeNo The store number selected by the canvasser.
   * @returns An Observable emitting the string response returned by the backend.
   */
  addLoginStore(canvasserCode: string, storeNo: string): Observable<string> {
    const url = `${environment.PplAppsApi}/LoginStore/Add`;

    const httpOptions = { headers: this.headers };

    let LoginStore = <LoginStore>{};
    let datepipe = new DatePipe('en-US');

    LoginStore.canvasserCode = canvasserCode;
    LoginStore.canvasserStoreNo = storeNo;
    LoginStore.loginDate = datepipe.transform(
      new Date(),
      'yyyy-MM-ddTHH:mm:ss+02:00'
    );

    return this.http.post<string>(url, LoginStore, httpOptions);
  }

  /**
   * Fetches the application flow properties configured for the given store and
   * company. These properties drive the behaviour of the app selection screen
   * and downstream applications (for example, enabling or disabling specific
   * features per store).
   * @param storeNo The store number for which to fetch flow properties.
   * @param companyNo The company number owning the store.
   * @returns An Observable emitting the ApplicationFlowProperties for the store/company.
   */
  getApplicationFlowProperties(storeNo: number, companyNo: number): Observable<ApplicationFlowProperties> {
    const url = `${environment.PplAppsApi}/LoginStore/GetApplicationFlowProperties/${storeNo}/${companyNo}`;

    const httpOptions = { headers: this.headers };

    return this.http.get<ApplicationFlowProperties>(url, httpOptions);
  }
}
